import React, { useEffect, useState } from "react";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useToast } from "context/ToastContext";

interface Store {
 _id: string;
 name: string;
 domain: string;
 subdomain?: string;
 businessModel: string;
 businessType?: string;
 subscriptionStatus: string;
 isActive: boolean;
 status?: "pending" | "approved" | "rejected";
 pendingAdminEmail?: string;
 pendingAdminName?: string;
}

const PlatformStores: React.FC = () => {
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const { addToast } = useToast();
 const [stores, setStores] = useState<Store[]>([]);
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const [approvalFilter, setApprovalFilter] = useState<string>("all"); // "all", "pending", "approved", "rejected"
 const [processingStoreId, setProcessingStoreId] = useState<string | null>(null);
 const [storeToReject, setStoreToReject] = useState<string | null>(null);

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchStores();
 }, [isAuthenticated, navigate, page, approvalFilter]);

 const fetchStores = async () => {
  try {
   setLoading(true);
   const params: any = { page, limit: 20 };
   if (approvalFilter !== "all") {
    params.approvalStatus = approvalFilter;
   }
   const response = await platformApiService.getStores(params);
   setStores(response.data.stores);
   setTotalPages(response.data.pagination?.pages || 1);
  } catch (error) {
   console.error("Error fetching stores:", error);
  } finally {
   setLoading(false);
  }
 };

 const toggleStoreStatus = async (storeId: string, currentStatus: boolean) => {
  try {
   await platformApiService.toggleStoreStatus(storeId, !currentStatus);
   fetchStores();
  } catch (error) {
   console.error("Error toggling store status:", error);
  }
 };

 const handleApproveStore = async (storeId: string, action: "approve" | "reject") => {
  try {
   setProcessingStoreId(storeId);
   const store = stores.find((s) => s._id === storeId);
   await platformApiService.approveStore(
    storeId,
    action,
    store?.pendingAdminEmail,
    store?.pendingAdminName
   );
   await fetchStores();
   addToast(
    action === "approve"
     ? "Store approved successfully! Credentials have been sent to the store admin."
     : "Store rejected.",
    action === "approve" ? "success" : "info"
   );
   setStoreToReject(null);
  } catch (error: any) {
   console.error(`Error ${action === "approve" ? "approving" : "rejecting"} store:`, error);
   addToast(
    error.response?.data?.error || `Failed to ${action} store. Please try again.`,
    "error"
   );
  } finally {
   setProcessingStoreId(null);
  }
 };

 const confirmReject = (storeId: string) => {
  setStoreToReject(storeId);
 };

 if (loading) {
  return <div className="p-8">Loading stores...</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">Stores</h1>
    <button
     onClick={() => navigate("/platform/stores/new")}
     className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
    >
     Create Store
    </button>
   </div>

   {/* Approval Status Filter */}
   <div className="mb-4 flex gap-2">
    <button
     onClick={() => setApprovalFilter("all")}
     className={`px-4 py-2 rounded ${
      approvalFilter === "all"
       ? "bg-blue-600 text-white"
       : "bg-gray-200 text-gray-700 hover:bg-gray-300"
     }`}
    >
     All Stores
    </button>
    <button
     onClick={() => setApprovalFilter("pending")}
     className={`px-4 py-2 rounded ${
      approvalFilter === "pending"
       ? "bg-yellow-600 text-white"
       : "bg-gray-200 text-gray-700 hover:bg-gray-300"
     }`}
    >
     Pending Approval
    </button>
    <button
     onClick={() => setApprovalFilter("approved")}
     className={`px-4 py-2 rounded ${
      approvalFilter === "approved"
       ? "bg-green-600 text-white"
       : "bg-gray-200 text-gray-700 hover:bg-gray-300"
     }`}
    >
     Approved
    </button>
    <button
     onClick={() => setApprovalFilter("rejected")}
     className={`px-4 py-2 rounded ${
      approvalFilter === "rejected"
       ? "bg-red-600 text-white"
       : "bg-gray-200 text-gray-700 hover:bg-gray-300"
     }`}
    >
     Rejected
    </button>
   </div>

   <div className="bg-white shadow overflow-hidden">
    <div className="overflow-x-auto">
     <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
       <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 max-w-xs">
         Name
        </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Domain
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Business Model
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Approval Status
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Subscription Status
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Actions
       </th>
      </tr>
     </thead>
     <tbody className="bg-white divide-y divide-gray-200">
      {stores.length === 0 ? (
       <tr>
        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
         {approvalFilter === "all"
          ? "No stores found."
          : approvalFilter === "pending"
          ? "No pending stores."
          : approvalFilter === "approved"
          ? "No approved stores."
          : "No rejected stores."}
        </td>
       </tr>
      ) : (
       stores.map((store) => (
       <tr key={store._id}>
        <td className="px-6 py-4 w-1/4 max-w-xs">
         <Link
          to={`/platform/stores/${store._id}`}
          className="text-indigo-600 hover:text-indigo-900 font-medium block truncate"
          title={store.name}
         >
          {store.name}
         </Link>
         {store.status === "pending" && store.pendingAdminEmail && (
          <div className="text-xs text-gray-500 mt-1 truncate" title={store.pendingAdminEmail}>
           Admin: {store.pendingAdminEmail}
           {store.pendingAdminName && ` (${store.pendingAdminName})`}
          </div>
         )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
         {store.domain || store.subdomain || "N/A"}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {store.businessModel}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
         <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded ${
           store.status === "pending"
            ? "bg-yellow-100 text-yellow-800"
            : store.status === "approved"
            ? "bg-green-100 text-green-800"
            : store.status === "rejected"
            ? "bg-red-100 text-red-800"
            : "bg-gray-100 text-gray-800"
          }`}
         >
          {store.status || "N/A"}
         </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
         <span
          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded ${
           store.isActive && store.subscriptionStatus === "active"
            ? "bg-green-100 text-green-800"
            : store.subscriptionStatus === "trialing"
            ? "bg-blue-100 text-blue-800"
            : "bg-gray-100 text-gray-800"
          }`}
         >
          {store.subscriptionStatus || "N/A"}
         </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
         {store.status === "pending" ? (
          <div className="flex gap-2">
           <button
            onClick={() => handleApproveStore(store._id, "approve")}
            disabled={processingStoreId === store._id}
            className="px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
           >
            {processingStoreId === store._id ? "Processing..." : "Approve"}
           </button>
           <button
            onClick={() => confirmReject(store._id)}
            disabled={processingStoreId === store._id}
            className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
           >
            Reject
           </button>
          </div>
         ) : (
          <>
           <Link
            to={`/platform/stores/${store._id}`}
            className="text-indigo-600 hover:text-indigo-900 mr-4"
           >
            View
           </Link>
           <Link
            to={`/platform/stores/${store._id}/edit`}
            className="text-indigo-600 hover:text-indigo-900 mr-4"
           >
            Edit
           </Link>
           <button
            onClick={() => toggleStoreStatus(store._id, store.isActive)}
            className={`${
             store.isActive ? "text-red-600" : "text-green-600"
            } hover:text-red-900`}
           >
            {store.isActive ? "Suspend" : "Activate"}
           </button>
          </>
         )}
        </td>
       </tr>
       ))
      )}
     </tbody>
    </table>
    </div>
   </div>

   {totalPages > 1 && (
    <div className="mt-4 flex justify-center">
     <button
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      disabled={page === 1}
      className="px-4 py-2 border disabled:opacity-50"
     >
      Previous
     </button>
     <span className="px-4 py-2 border-t border-b">
      Page {page} of {totalPages}
     </span>
     <button
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      disabled={page === totalPages}
      className="px-4 py-2 border disabled:opacity-50"
     >
      Next
     </button>
    </div>
   )}

   {/* Rejection Confirmation Modal */}
   {storeToReject && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
     <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">Confirm Rejection</h3>
      <p className="text-gray-700 mb-6">
       Are you sure you want to reject this store? This action cannot be undone.
      </p>
      <div className="flex justify-end gap-3">
       <button
        onClick={() => setStoreToReject(null)}
        className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded"
       >
        Cancel
       </button>
       <button
        onClick={() => {
         if (storeToReject) {
          handleApproveStore(storeToReject, "reject");
         }
        }}
        disabled={processingStoreId === storeToReject}
        className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
       >
        {processingStoreId === storeToReject ? "Rejecting..." : "Reject Store"}
       </button>
      </div>
     </div>
    </div>
   )}
  </div>
 );
};

export default PlatformStores;

